import {
  percent,
  ratio,
  shiftDate,
  storeDate,
  type DeliveryReport,
  type DeliveryReportRow,
  type EscalationFigures,
  type EscalationReportRow,
  type IsoDate,
} from "@estelle/domain";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { ForbiddenError } from "@/adapters/api/base";
import { useDataSource } from "@/adapters/data-source";
import { ChartCard, FigureTable } from "@/components/charts/ChartCard";
import {
  EmptyState,
  LoadFailed,
  NoAccess,
} from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { reportingNow } from "@/lib/reportClock";
import { cn } from "@/lib/utils";
import {
  courierLabel,
  durationLabel,
  percentLabel,
} from "@/modules/dashboard/format";
import { DateRangeControls } from "@/modules/reports/DateRangeControls";
import { ReportHeader } from "@/modules/reports/ReportHeader";
import { rangeLabel } from "@/modules/reports/orderChart";
import {
  effectiveSalesRange,
  encodeSalesRange,
  readSalesRange,
  salesPresets,
} from "@/modules/reports/sales";
import { useSectionSearch, type SectionSearch } from "@/routes/search";

/**
 * Delivery and speed (#528): RTO rate, delivery success, first-attempt rate and
 * the two speeds for a range of the store's days, as one total and split by
 * courier, by destination state and by payment method. This is where the
 * dashboard's Delivery and Speed cards land.
 *
 * Counted on the server (`dashboard.deliveryReport`), so a range of a year is
 * a few kilobytes and no shipment crosses to the browser. The dates live in the
 * address bar, the same `chart` parameter the Sales report keeps its range in.
 */
export function Delivery() {
  const data = useDataSource();
  const navigate = useNavigate();
  const urlSearch = useSectionSearch();

  const today = storeDate(reportingNow());
  const read = readSalesRange(urlSearch.chart);
  // The report opens on the last 30 days, like the dashboard's slowest period
  // short of a quarter, not on the Sales report's 60.
  const range = effectiveSalesRange(
    read.range ?? { from: shiftDate(today, -29), to: today },
    today,
  );
  const presets = useMemo(() => salesPresets(today), [today]);

  const [notice, setNotice] = useState<string | null>(null);
  const { problem } = read;
  const [reported, setReported] = useState<string | null>(null);
  if (problem !== reported) {
    setReported(problem);
    if (problem !== null) {
      setNotice(`${problem} The last 30 days are shown instead.`);
    }
  }
  useEffect(() => {
    if (problem === null) return;
    void navigate({
      to: ".",
      replace: true,
      search: (prev: SectionSearch) => ({ ...prev, chart: undefined }),
    });
  }, [problem, navigate]);

  function setRange(next: { from: IsoDate; to: IsoDate }) {
    setNotice(null);
    void navigate({
      to: ".",
      search: (prev: SectionSearch) => ({
        ...prev,
        chart: encodeSalesRange(next),
      }),
    });
  }

  const query = useQuery({
    queryKey: ["reports", "delivery", range.from, range.to],
    queryFn: () => data.dashboard.deliveryReport(range),
    placeholderData: keepPreviousData,
  });

  const controls = (
    <ReportHeader
      readAt={query.data ? query.dataUpdatedAt : undefined}
      controls={
        <section aria-label="Report dates">
          <DateRangeControls
            idPrefix="delivery"
            range={range}
            today={today}
            presets={presets}
            onChange={setRange}
            notice={notice}
            align="end"
          />
        </section>
      }
      footnote="Days run midnight to midnight, India time. A shipment counts when it reaches its outcome (delivered or RTO) in these dates; speed counts the shipments that left, or arrived, in them."
    />
  );

  if (query.error instanceof ForbiddenError) {
    return (
      <NoAccess
        permission={query.error.permission ?? "orders.shipments.view"}
      />
    );
  }

  if (query.isError) {
    return (
      <div className="flex flex-col gap-6">
        {controls}
        <LoadFailed
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-6">
        {controls}
        <Card>
          <CardContent>
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>
        <TableSkeleton />
      </div>
    );
  }

  const report = query.data;
  const empty =
    report.totals.outcomes === 0 &&
    report.totals.orderToShipCount === 0 &&
    report.totals.shipToDeliverCount === 0;

  return (
    <div className="flex flex-col gap-6">
      {controls}
      {empty ? (
        <EmptyState
          title="No shipments in these dates"
          description={`No shipment left, was delivered or came back between ${rangeLabel(report.from, report.to)}.`}
        />
      ) : (
        <div
          aria-busy={query.isPlaceholderData}
          className={cn(
            "flex flex-col gap-6 transition-opacity",
            query.isPlaceholderData && "opacity-50",
          )}
        >
          <Totals report={report} />
          <Breakdown
            title="By courier"
            first="Courier"
            rows={report.byCourier}
            label={courierLabel}
          />
          <Breakdown
            title="By destination state"
            first="State"
            rows={report.byState}
          />
          <Breakdown
            title="By payment method"
            first="Payment"
            rows={report.byPayment}
          />
          <EscalationsByCourier report={report} />
        </div>
      )}
    </div>
  );
}

const rate = (part: number, whole: number) => percent(ratio(part, whole));

function Totals({ report }: { report: DeliveryReport }) {
  const t = report.totals;
  const figures: { label: string; value: string; note: string }[] = [
    {
      label: "RTO rate",
      value: percentLabel(rate(t.rto, t.outcomes)),
      note: `n = ${String(t.outcomes)} shipments. Ended as RTO ÷ delivered or RTO.`,
    },
    {
      label: "Delivery success",
      value: percentLabel(rate(t.delivered, t.outcomes)),
      note: `n = ${String(t.outcomes)} shipments. Delivered ÷ delivered or RTO.`,
    },
    {
      label: "First-attempt delivery",
      value: percentLabel(rate(t.deliveredFirstAttempt, t.delivered)),
      note: `n = ${String(t.delivered)} delivered. Delivered on the first attempt ÷ delivered.`,
    },
    {
      label: "Order to ship (median)",
      value: durationLabel(t.orderToShipHours),
      note: `n = ${String(t.orderToShipCount)} shipments. Order placed to shipment leaving.`,
    },
    {
      label: "Ship to deliver (median)",
      value: durationLabel(t.shipToDeliverHours),
      note: `n = ${String(t.shipToDeliverCount)} shipments. Shipment leaving to delivered.`,
    },
  ];
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
      {figures.map((figure) => (
        <li
          key={figure.label}
          className="bg-card flex min-w-0 flex-col gap-1 rounded-md border p-3"
        >
          <span className="text-muted-foreground text-xs">{figure.label}</span>
          <span className="text-2xl font-semibold tabular-nums">
            {figure.value}
          </span>
          <span className="text-muted-foreground text-xs">{figure.note}</span>
        </li>
      ))}
    </ul>
  );
}

function Breakdown({
  title,
  first,
  rows,
  label = (key) => key,
}: {
  title: string;
  first: string;
  rows: readonly DeliveryReportRow[];
  label?: (key: string) => string;
}) {
  return (
    <ChartCard
      title={title}
      description="Highest number of shipments with an outcome first. n is what each rate is over."
    >
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing to split.</p>
      ) : (
        <div className="overflow-x-auto">
          <FigureTable
            rows={rows}
            rowKey={(row) => row.key}
            columns={[
              { header: first, cell: (row) => label(row.key), strong: true },
              {
                header: "Shipments (n)",
                numeric: true,
                cell: (row) => row.outcomes,
              },
              {
                header: "RTO rate",
                numeric: true,
                cell: (row) => percentLabel(rate(row.rto, row.outcomes)),
              },
              {
                header: "Delivery success",
                numeric: true,
                cell: (row) => percentLabel(rate(row.delivered, row.outcomes)),
              },
              {
                header: "First attempt",
                numeric: true,
                cell: (row) =>
                  percentLabel(rate(row.deliveredFirstAttempt, row.delivered)),
              },
              {
                header: "Order to ship",
                numeric: true,
                cell: (row) => durationLabel(row.orderToShipHours),
              },
              {
                header: "Ship to deliver",
                numeric: true,
                cell: (row) => durationLabel(row.shipToDeliverHours),
              },
            ]}
          />
        </div>
      )}
    </ChartCard>
  );
}

/** "1.5 d", or a dash when nothing closed to measure. */
const daysLabel = (value: number | null): string =>
  value === null ? "—" : `${value.toFixed(1)} d`;

const share = (part: number, whole: number): string =>
  percentLabel(rate(part, whole));

function levelCell(figures: EscalationFigures, level: 1 | 2 | 3): string {
  const row = figures.byLevel.find((entry) => entry.level === level);
  return row && row.resolved > 0
    ? `${daysLabel(row.medianDays)} / ${daysLabel(row.slowestDays)} (n = ${String(row.resolved)})`
    : "—";
}

/**
 * Escalations by courier (#702): how many parcels each courier made the team
 * chase, how fast each fixed them and how they ended. A person's Stop tracking
 * is left out of every figure and shown as its own count.
 */
function EscalationsByCourier({ report }: { report: DeliveryReport }) {
  const rows: EscalationReportRow[] = report.escalations.byCourier;
  const t = report.escalations.totals;
  return (
    <ChartCard
      title="Escalations by courier"
      description={`${String(t.escalations)} opened in these dates (n), ${String(t.dismissed)} dismissed and left out. Days are first opened to closed: median / slowest 10%. Open now counts every date.`}
    >
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No escalations in these dates.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <FigureTable
            rows={rows}
            rowKey={(row) => row.key}
            columns={[
              {
                header: "Courier",
                cell: (row) => courierLabel(row.key),
                strong: true,
              },
              {
                header: "Escalations (n)",
                numeric: true,
                cell: (row) => row.escalations,
              },
              {
                header: "Days to resolve",
                numeric: true,
                cell: (row) =>
                  row.resolved > 0
                    ? `${daysLabel(row.medianDays)} / ${daysLabel(row.slowestDays)} (n = ${String(row.resolved)})`
                    : "—",
              },
              {
                header: "Level 1",
                numeric: true,
                cell: (row) => levelCell(row, 1),
              },
              {
                header: "Level 2",
                numeric: true,
                cell: (row) => levelCell(row, 2),
              },
              {
                header: "Level 3",
                numeric: true,
                cell: (row) => levelCell(row, 3),
              },
              {
                header: "Delivered",
                numeric: true,
                cell: (row) => share(row.endedDelivered, row.resolved),
              },
              {
                header: "RTO",
                numeric: true,
                cell: (row) => share(row.endedRto, row.resolved),
              },
              {
                header: "Lost",
                numeric: true,
                cell: (row) => share(row.endedLost, row.resolved),
              },
              {
                header: "Escalation rate",
                numeric: true,
                cell: (row) =>
                  row.shipped > 0
                    ? `${share(row.escalatedShipments, row.shipped)} (n = ${String(row.shipped)})`
                    : "—",
              },
              {
                header: "Open now (>3 d, >7 d)",
                numeric: true,
                cell: (row) =>
                  `${String(row.openNow)} (${String(row.openOver3)}, ${String(row.openOver7)})`,
              },
              {
                header: "Sent to resolved",
                numeric: true,
                cell: (row) =>
                  row.sentToResolvedCount > 0
                    ? `${daysLabel(row.medianDaysSentToResolved)} (n = ${String(row.sentToResolvedCount)})`
                    : "—",
              },
              {
                header: "Dismissed",
                numeric: true,
                cell: (row) => row.dismissed,
              },
            ]}
          />
        </div>
      )}
    </ChartCard>
  );
}
